import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { FileDown, ArrowLeft, Package } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-[#F4A261]/20 text-[#E76F51]",
  confirmed: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
  preparing: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
  delivered: "bg-[#2D6A4F] text-white",
  cancelled: "bg-[#D62828]/20 text-[#D62828]",
};

const STATUS_LABEL = {
  pending: "En attente",
  confirmed: "Confirmée",
  preparing: "En préparation",
  delivered: "Livrée",
  cancelled: "Annulée",
};

export function OrderListPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } catch {
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="orders-page">
      <Header />
      <section className="ff-container py-12">
        <p className="ff-overline">Historique</p>
        <h1 className="font-serif text-4xl lg:text-5xl mt-3">Mes commandes</h1>

        {loading ? (
          <p className="mt-10 text-[#8A9A8E]">Chargement...</p>
        ) : orders.length === 0 ? (
          <div className="mt-16 text-center" data-testid="orders-empty">
            <Package className="w-12 h-12 text-[#8A9A8E] mx-auto" />
            <p className="mt-4 text-[#4A5D4E]">Aucune commande pour le moment.</p>
            <Link to="/shop" className="ff-btn-primary mt-6 inline-flex">Passer ma première commande</Link>
          </div>
        ) : (
          <div className="mt-10 space-y-3">
            {orders.map((o) => (
              <Link
                to={`/orders/${o.id}`}
                key={o.id}
                className="ff-card p-5 flex items-center justify-between block"
                data-testid={`order-row-${o.id}`}
              >
                <div>
                  <p className="font-mono text-sm text-[#1A2B1E]">{o.order_number}</p>
                  <p className="text-xs text-[#8A9A8E] mt-1">{o.created_at?.slice(0, 10)}</p>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-[#4A5D4E]">{o.items.length} produit(s)</p>
                </div>
                <span className={`text-xs px-3 py-1.5 ${STATUS_COLORS[o.status] || ""}`}>
                  {STATUS_LABEL[o.status] || o.status}
                </span>
                <div className="font-serif text-xl text-[#2D6A4F]">CHF {o.total.toFixed(2)}</div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch {
        toast.error("Commande introuvable");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <div data-testid="order-detail-page">
      <Header />
      <section className="ff-container py-12 max-w-4xl">
        <Link to="/orders" className="inline-flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#2D6A4F]" data-testid="order-back-link">
          <ArrowLeft className="w-4 h-4" /> Mes commandes
        </Link>

        {loading || !order ? (
          <p className="mt-10 text-[#8A9A8E]">Chargement...</p>
        ) : (
          <div className="mt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="ff-overline">Commande</p>
                <h1 className="font-serif text-4xl mt-2" data-testid="order-number">{order.order_number}</h1>
                <p className="text-sm text-[#8A9A8E] mt-2">Passée le {order.created_at.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-3 py-1.5 ${STATUS_COLORS[order.status] || ""}`} data-testid="order-status">
                  {STATUS_LABEL[order.status] || order.status}
                </span>
                <a
                  href={`${API}/orders/${order.id}/invoice.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="ff-btn-secondary text-sm"
                  data-testid="order-download-invoice"
                >
                  <FileDown className="w-4 h-4" /> Facture PDF
                </a>
                <a
                  href={`${API}/orders/${order.id}/receipt.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="ff-btn-primary text-sm"
                  data-testid="order-download-receipt"
                >
                  <FileDown className="w-4 h-4" /> Reçu PDF
                </a>
              </div>
            </div>

            <div className="mt-8 ff-card p-6">
              <p className="ff-overline">Articles</p>
              <table className="w-full mt-4 text-sm">
                <thead>
                  <tr className="text-left text-[#8A9A8E] border-b border-[#E5E3DC]">
                    <th className="py-2">Produit</th>
                    <th>Unité</th>
                    <th className="text-right">Qté</th>
                    <th className="text-right">Prix</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((i, idx) => (
                    <tr key={idx} className="border-b border-[#F2EFEB]">
                      <td className="py-3">{i.name}</td>
                      <td>{i.unit}</td>
                      <td className="text-right">{i.quantity}</td>
                      <td className="text-right">CHF {i.price.toFixed(2)}</td>
                      <td className="text-right">CHF {(i.price * i.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-5 space-y-1 text-sm">
                <div className="flex justify-between"><span>Sous-total</span><span>CHF {order.subtotal.toFixed(2)}</span></div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-[#E76F51]">
                    <span>{order.discount_label || "Remise"}</span>
                    <span>- CHF {order.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-lg pt-3 border-t border-[#E5E3DC] mt-2">
                  <span>Total TTC</span>
                  <span className="font-serif text-2xl text-[#2D6A4F]">CHF {order.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-[#8A9A8E]"><span>dont TVA 8.1% incluse</span><span>CHF {order.vat.toFixed(2)}</span></div>
              </div>
            </div>

            {!order.shipping_same_as_billing && (
              <div className="mt-6 ff-card p-6">
                <p className="ff-overline">Livraison à une autre adresse</p>
                <div className="mt-3 text-sm">
                  <p>{order.shipping_recipient}</p>
                  <p>{order.shipping_address}</p>
                  <p>{order.shipping_postal_code} {order.shipping_city}</p>
                </div>
              </div>
            )}

            {order.notes && (
              <div className="mt-6 ff-card p-6">
                <p className="ff-overline">Notes</p>
                <p className="mt-3 text-sm text-[#4A5D4E]">{order.notes}</p>
              </div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
